import React, { useEffect, useState } from "react";
import { X, Filter } from "lucide-react";
import styles from "./FilterPanel.module.scss";
import { useFilterStore } from "../../../../supplierEcommerce/store";

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  options?: FilterOptions[];
}

const parseBound = (value: string): number | null => {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function FilterPanel({
  isOpen,
  onClose,
  options,
}: FilterPanelProps) {
  const applyArrayFilter = useFilterStore((state) => state.applyArrayFilter);
  const setUnitPrice = useFilterStore((state) => state.setUnitPrice);
  const clearFilters = useFilterStore((state) => state.clearFilters);
  const filter = useFilterStore((state) => state.filter);

  const categoryOptions =
    options?.find((opt) => opt.id === "category")?.options || [];
  const brandOptions =
    options?.find((opt) => opt.id === "brand")?.options || [];
  const lineOptions = options?.find((opt) => opt.id === "line")?.options || [];

  const [unitPriceMin, setUnitPriceMin] = useState<string>("");
  const [unitPriceMax, setUnitPriceMax] = useState<string>("");

  useEffect(() => {
    const [min, max] = filter?.unitPrice ?? [null, null];
    setUnitPriceMin(min === null ? "" : min.toString());
    setUnitPriceMax(max === null ? "" : max.toString());
  }, [filter?.unitPrice]);

  if (!isOpen) return null;

  const handleToggle = (
    category: "category" | "brand" | "line",
    value: string
  ) => {
    applyArrayFilter(category, value);
  };

  const handleUnitPriceChange = (min: string, max: string) => {
    setUnitPriceMin(min);
    setUnitPriceMax(max);
    const minVal = parseBound(min);
    const maxVal = parseBound(max);
    if (min !== "" && minVal === null) return;
    if (max !== "" && maxVal === null) return;
    if (minVal !== null && maxVal !== null && maxVal < minVal) return;
    setUnitPrice([minVal, maxVal]);
  };

  return (
    <aside className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Filter className={styles.headerIcon} />
            <h3 className={styles.title}>Filtros</h3>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X className={styles.closeIcon} />
          </button>
        </div>
        <p className={styles.subtitle}>Refina tu búsqueda de productos</p>
      </div>

      <div className={styles.content}>
        {categoryOptions.length > 0 && (
          <div className={styles.filterSection}>
            <h4 className={styles.filterTitle}>Categoría</h4>
            <div className={styles.filterOptions}>
              {categoryOptions.map((category) => (
                <div key={category.value} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id={`category-${category.value}`}
                    className={styles.checkbox}
                    onChange={() => handleToggle("category", category.value)}
                    checked={(filter.category ?? []).includes(category.value)}
                  />
                  <label
                    htmlFor={`category-${category.value}`}
                    className={styles.label}
                  >
                    {category.label ?? category.value}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.separator} />

        {lineOptions.length > 0 && (
          <div className={styles.filterSection}>
            <h4 className={styles.filterTitle}>Línea</h4>
            <div className={styles.filterOptions}>
              {lineOptions.map((line) => (
                <div key={line.value} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id={`line-${line.value}`}
                    className={styles.checkbox}
                    onChange={() => handleToggle("line", line.value)}
                    checked={filter.line.includes(line.value)}
                  />
                  <label
                    htmlFor={`line-${line.value}`}
                    className={styles.label}
                  >
                    {line.label ?? line.value}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.separator} />

        {brandOptions.length > 0 && (
          <div className={styles.filterSection}>
            <h4 className={styles.filterTitle}>Marca</h4>
            <div className={styles.filterOptions}>
              {brandOptions.map((brand) => (
                <div key={brand.value} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id={`brand-${brand.value}`}
                    className={styles.checkbox}
                    onChange={() => handleToggle("brand", brand.value)}
                    checked={filter.brand.includes(brand.value)}
                  />
                  <label
                    htmlFor={`brand-${brand.value}`}
                    className={styles.label}
                  >
                    {brand.label ?? brand.value}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.separator} />

        <div className={styles.filterSection}>
          <h4 className={styles.filterTitle}>Precio unitario</h4>
          <div className={styles.rangeInputs}>
            <div className={styles.inputGroup}>
              <label htmlFor="unit-price-min" className={styles.inputLabel}>
                Mínimo
              </label>
              <input
                type="number"
                id="unit-price-min"
                placeholder="0"
                className={styles.input}
                value={unitPriceMin}
                onChange={(e) =>
                  handleUnitPriceChange(e.target.value, unitPriceMax)
                }
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="unit-price-max" className={styles.inputLabel}>
                Máximo
              </label>
              <input
                type="number"
                id="unit-price-max"
                placeholder="1,000,000"
                className={styles.input}
                value={unitPriceMax}
                onChange={(e) =>
                  handleUnitPriceChange(unitPriceMin, e.target.value)
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.separator} />
      </div>

      <div className={styles.footer}>
        <button className={styles.applyButton} onClick={() => onClose()}>
          Aplicar filtros
        </button>
        <button
          className={styles.clearButton}
          onClick={() => {
            clearFilters();
            setUnitPriceMin("");
            setUnitPriceMax("");
          }}
        >
          Limpiar todo
        </button>
      </div>
    </aside>
  );
}
